import { useFormik } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

import { RejectionReasonsCodeType } from '@percent/admin-dashboard/api/types'
import { Dialog, Loader } from '@percent/admin-dashboard/common/components'
import { FieldDropDown } from '@percent/admin-dashboard/common/components/fieldDropDown/FieldDropDown'
import { ActionsDialogAction } from '@percent/admin-dashboard/common/components/dialog/actionDialog/ActionDialog'
import { useMutation } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { LocaleKey } from '@percent/admin-dashboard/i18n/config'
import { Box, Button, Spacer, Typography } from '@percent/lemonade'
import styles from './Dialog.module.scss'
import { RejectModalProps } from './Dialog.types'
import { mapRejectionReasonCode } from './mapRejectionReasonCode'

export function RejectModal({
  isOpened,
  setIsOpened,
  finishAction,
  nonprofitName,
  validationRequestId
}: RejectModalProps) {
  const { t } = useTranslation()
  const { adminService } = useServices()

  const [{ errorMessage, isLoading, success }, { apiFunc }] = useMutation(
    adminService.rejectValidationRequest,
    finishAction
  )
  const { values, setFieldValue, isValid, dirty } = useFormik({
    initialValues: {
      rejectionReasonCode: ''
    },
    validateOnMount: true,
    validationSchema: () =>
      yup.object().shape({
        rejectionReasonCode: yup.mixed().oneOf(Object.values(RejectionReasonsCodeType))
      }),
    onSubmit: () => {}
  })

  const mapRejectionReasonsCode = (rejectionReasonsCode: RejectionReasonsCodeType[]) =>
    rejectionReasonsCode.map(rejectionReasonCode => ({
      title: t(mapRejectionReasonCode[rejectionReasonCode] as LocaleKey),
      value: rejectionReasonCode
    }))

  const { rejectionReasonCode } = values

  const handleSubmit = () => apiFunc({ payload: { rejectionReasonCode }, id: validationRequestId })

  const rejectionReasonsCodes = Object.values(RejectionReasonsCodeType)

  return (
    <Dialog
      openModal={isOpened}
      onClose={() => setIsOpened(false)}
      headerTitle={t('dialog.rejectValidation')}
      withoutHeader={success || !!errorMessage}
    >
      {isLoading ? (
        <Loader />
      ) : errorMessage ? (
        <ActionsDialogAction
          handleOnClose={() => setIsOpened(false)}
          description={errorMessage}
          title={t('dialog.somethingWentWrong')}
          buttonTitle={t('button.done')}
          successIcon={false}
        />
      ) : success ? (
        <ActionsDialogAction
          handleOnClose={() => setIsOpened(false)}
          description={`${nonprofitName} ${t('dialog.organisationHasBeenAddedToNonValid')}`}
          title={t('dialog.hasBeenRejected')}
          buttonTitle={t('button.done')}
          successIcon={false}
        />
      ) : (
        <>
          <Box className={styles.modalBody}>
            <Typography variant="large">
              <span style={{ fontFamily: 'MarkProMedium' }}>{nonprofitName} </span>
              {t('dialog.willBeRejected')}
            </Typography>
          </Box>
          <Box className={styles.form}>
            <FieldDropDown
              selectTitle={t('form.reasonForRejection')}
              initialValue={rejectionReasonCode}
              placeholder={t('form.selectRejectionReason')}
              onClick={(
                e: React.ChangeEvent<{
                  name?: string | undefined | null
                  value: unknown
                }>
              ) => setFieldValue('rejectionReasonCode', e.target.value)}
              valueArray={mapRejectionReasonsCode(rejectionReasonsCodes)}
            />
          </Box>
          <Box className={styles.dialogFooter}>
            <Button
              disabled={!(dirty && isValid)}
              type="submit"
              variant="primary"
              data-testid="btn-reject-validation"
              loading={isLoading}
              onClick={handleSubmit}
            >
              {t('button.reject')}
            </Button>
            <Spacer size={2} />
            <Button
              variant="secondary"
              data-testid="btn-cancel-validiation"
              onClick={() => {
                setIsOpened(false)
              }}
            >
              {t('button.cancel')}
            </Button>
          </Box>
        </>
      )}
    </Dialog>
  )
}
